import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { errorMessage } from '@/api/errors';
import { AlertBox } from '@/common/AlertBox';
import { InfoSectionContainer } from '@/common/InfoSection';
import { TabsBar } from '@/common/TabsBar';
import { loginInformationErrorKey } from '@/settings/loginInformationErrors';
import {
  type LoginInfoField,
  SettingsLoginInfo,
} from '@/settings/SettingsLoginInfo';
import { FormTitle, PageContent, PageHeader } from '@/settings/settingsStyles';
import { getMemberSettingsTabs } from '@/settings/tabData';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { Title } from '~/styles/layoutElements';
import { asText } from '~/utils/searchParams';

interface SettingsSearch {
  /** Legacy's parameter name. */
  verify_email_change?: string;
}

export const Route = createFileRoute(
  '/_app/_users/members/$memberId/settings/',
)({
  component: SettingsRoute,
  validateSearch: (search: Record<string, unknown>): SettingsSearch => ({
    verify_email_change: asText(search.verify_email_change),
  }),
});

interface Feedback {
  variant: 'success' | 'error';
  message: string;
}

const EMPTY_PASSWORDS = {
  currentPassword: '',
  newPassword: '',
  repeatPassword: '',
};

function SettingsRoute(): ReactElement | null {
  const { t } = useTranslation();
  const { memberId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { me } = useCurrentUser();

  const [passwords, setPasswords] = useState(EMPTY_PASSWORDS);
  // `null` means "showing the stored address". Starting an email change doesn't
  // change the stored email, so clearing the draft on success makes the field
  // fall back to the old address, as legacy's form does.
  const [emailDraft, setEmailDraft] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const { mutateAsync: updateLoginInformation, isPending: isSaving } =
    api.updateMyLoginInformation.useMutation();
  const { mutateAsync: verifyEmailChange } =
    api.verifyMyEmailChange.useMutation();

  // The key is single-use: a successful verify clears the pending change, so a
  // second call 400s and would show the failure over a verification that
  // worked. StrictMode runs a fresh mount's effects twice in dev, so this latch
  // is needed.
  const verifyStarted = useRef(false);
  const verifyKey = search.verify_email_change;

  useEffect(() => {
    if (verifyKey === undefined || verifyStarted.current) return;
    verifyStarted.current = true;

    verifyEmailChange({ body: { key: verifyKey } })
      .then(async () => {
        await api.getCurrentUser.invalidateQueries();
        setFeedback({
          variant: 'success',
          message: t('notifications.you-have-successfully-verified'),
        });
        // The latch covers one mount; this covers a reload. Without it,
        // refreshing the success page re-fires a spent key and repaints the
        // error.
        await navigate({ to: './', search: {}, replace: true });
      })
      .catch(() => {
        setFeedback({
          variant: 'error',
          message: t('notifications.there-was-a-problem'),
        });
      });
  }, [verifyKey, verifyEmailChange, navigate, t]);

  if (me === undefined) return null;

  const accountEmail = emailDraft ?? me.email;

  const handleInputChange = (field: LoginInfoField, value: string): void => {
    if (field === 'accountEmail') {
      setEmailDraft(value);
      return;
    }
    setPasswords((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveChanges = async (): Promise<void> => {
    setFeedback(null);
    try {
      const result = await updateLoginInformation({
        body: {
          current_password: passwords.currentPassword,
          // Required on every submit, including a password-only one, as in
          // legacy.
          email: accountEmail,
          new_password:
            passwords.newPassword === '' ? null : passwords.newPassword,
          new_password_confirm:
            passwords.repeatPassword === '' ? null : passwords.repeatPassword,
        },
      });
      await api.getCurrentUser.invalidateQueries();
      setPasswords(EMPTY_PASSWORDS);
      setEmailDraft(null);
      const changed = result.password_changed || result.email_change_requested;
      setFeedback({
        // Legacy flashes the no-change message with error styling, so this does
        // too.
        variant: changed ? 'success' : 'error',
        message: changed
          ? t('notifications.your-settings-have-been')
          : t('notifications.no-changes-were-made'),
      });
    } catch (failure) {
      const key = loginInformationErrorKey(failure);
      setFeedback({
        variant: 'error',
        message:
          key === undefined
            ? errorMessage(failure, t('wbs.something-went-wrong-please'))
            : t(key),
      });
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <PageContent>
      {feedback !== null && (
        <AlertBox variant={feedback.variant}>{feedback.message}</AlertBox>
      )}
      <PageHeader>
        <Title>{t('wbs.account-settings')}</Title>
      </PageHeader>

      <div style={{ marginBottom: 11 }}>
        <TabsBar
          tabs={getMemberSettingsTabs(t)}
          base={`/members/${memberId}/settings`}
          selected="/"
        />
      </div>
      <InfoSectionContainer>
        <FormTitle>{t('wbs.login-information')}</FormTitle>
        {me.is_alias ? (
          // An alias account gets the notice and no form: its password was
          // generated and never shown to anyone, so it couldn't pass the
          // current-password check. The server refuses the write as well.
          <AlertBox variant="warning">{t('wbs.this-is-an-alias')}</AlertBox>
        ) : (
          <SettingsLoginInfo
            formData={{ ...passwords, accountEmail }}
            onInputChange={handleInputChange}
            onSaveChanges={handleSaveChanges}
            isSaving={isSaving}
          />
        )}
      </InfoSectionContainer>
    </PageContent>
  );
}
